/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import type { OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import {
    Button,
    Card,
    Dropdown,
    Label,
    makeStyles,
    Option,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    shorthands,
    tokens,
    useId,
} from '@fluentui/react-components';
import { WrenchRegular } from '@fluentui/react-icons';
import * as React from 'react';
import type { EntryPoint } from '../AppContainer';
import { EntryPoints } from '../AppContainer';

const useStyles = makeStyles({
    card: {
        ...shorthands.padding(tokens.spacingHorizontalL),
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
    },
    dropdown: {
        minWidth: '200px',
    },
});

interface DevButtonProps {
    onEntryPointChange: (entryPoint: EntryPoint) => void;
}

export const DevButton: React.FC<DevButtonProps> = ({ onEntryPointChange }) => {
    const styles = useStyles();
    const dropdownId = useId("entry-point-dropdown");

    const onOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
        if (data.optionValue && EntryPoints.includes(data.optionValue as EntryPoint)) {
            onEntryPointChange(data.optionValue as EntryPoint);
        }
    };

    return (
        <Popover>
            <PopoverTrigger disableButtonEnhancement>
                <Button icon={<WrenchRegular />} appearance="primary" size="large" shape="circular" />
            </PopoverTrigger>
            <PopoverSurface>
                <Card className={styles.card}>
                    <Label htmlFor={dropdownId}>Select Entry Point</Label>
                    <Dropdown
                        id={dropdownId}
                        aria-label="Select Entry Point"
                        onOptionSelect={onOptionSelect}
                        className={styles.dropdown}
                    >
                        {EntryPoints.map((ep) => (
                            <Option key={ep} value={ep}>
                                {ep}
                            </Option>
                        ))}
                    </Dropdown>
                </Card>
            </PopoverSurface>
        </Popover>
    );
};
